import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';
import clsx from 'clsx';
import { CmsFilters, CmsBrowsePage } from 'js/model/cms/cms-browse-page';
import { SearchOptions } from 'js/model/rainbow/browse-page/SearchOptions';
import { ViewMode } from 'js/pages/BrowsePage/ViewModeToggle';
import { BrowsePageOutput } from 'js/model/rainbow/browse-page/BrowsePageOutput';
import { Aggregations } from 'js/model/rainbow/browse-page/Aggregations';
import { StateData } from 'js/model/rainbow/StateData';
import { useDevice, useViewport } from '@treatwell/ui';
import { VenueListingSpecificationOutput } from 'js/model/rainbow/browse-page/VenueListingSpecificationOutput';
import { wwwTrackingContext } from './tracking';
import styles from './WhatWhereWhen.module.css';
import { StickyCollapse } from './StickyCollapse';
import { ContextualFilter } from './ContextualFilter';
import { WhatWhereWhen } from './WhatWhereWhen';

export interface HeroHandle {
  getHeightRef: () => React.RefObject<HTMLDivElement | null>;
}

interface Props {
  pageData: StateData;
  cms: CmsBrowsePage;
  browsePageOutput: BrowsePageOutput;
  aggregations: Aggregations;
  viewMode: ViewMode;
  openOnMount?: boolean;
  onViewModeChange: (mode: ViewMode) => void;
  collapseOnEvent?: string;
  onAnySelectFocus?: () => void;
  updateHeroHeight?: (height?: number) => void;
  hidden?: boolean;
}

export const Hero = forwardRef(
  (
    {
      browsePageOutput,
      updateHeroHeight,
      cms,
      openOnMount,
      collapseOnEvent,
      pageData,
      aggregations,
      viewMode,
      onViewModeChange,
      onAnySelectFocus,
      hidden,
    }: Props,
    ref: React.Ref<HeroHandle>
  ) => {
    const specificationRef = useRef<VenueListingSpecificationOutput>(
      browsePageOutput.specification
    );
    const heightRef = useRef<HTMLDivElement>(null);
    const { isMobile: isMobileDevice } = useDevice();
    const isMobile = useViewport({
      device: 'mobile',
      serverRender: isMobileDevice,
    });

    const updateHeroHeightHandler = useCallback(() => {
      if (updateHeroHeight) {
        updateHeroHeight(heightRef.current?.offsetHeight);
      }
    }, [updateHeroHeight]);

    const renderContent = (
      isCollapsed: boolean,
      collapse: (collapsed: boolean) => void,
      animating: boolean
    ) => {
      const wwwClassnames = clsx({
        [styles.collapsing]: animating && isCollapsed,
        [styles.expanding]: animating && !isCollapsed,
        [styles.collapsed]: isCollapsed,
      });

      const cmsFilters: CmsFilters = cms.page.browse.filters;
      const searchOptions: SearchOptions = browsePageOutput.searchOptions;
      const bgImageMobile = cmsFilters['whatwherewhen-background-image-mobile'];
      const bgImageNonMobile =
        cmsFilters['whatwherewhen-background-image-nonmobile'];
      const bgColour = cmsFilters['whatwherewhen-background-image-colour'];

      return (
        <div
          style={{
            backgroundColor: bgColour,
            backgroundImage: `url("${
              isMobile ? bgImageMobile : bgImageNonMobile
            }")`,
          }}
          className={styles.background}
        >
          <div className={clsx(styles.content, wwwClassnames)} ref={heightRef}>
            <WhatWhereWhen
              pageData={pageData}
              cms={cms}
              aggregations={aggregations}
              specification={browsePageOutput.specification}
              isCollapsed={isCollapsed}
              collapse={() => collapse(false)}
              searchOptions={searchOptions}
              viewMode={viewMode}
              onViewModeChange={onViewModeChange}
              onAnySelectFocus={onAnySelectFocus}
            />
            <ContextualFilter
              aggregations={aggregations}
              cmsFilters={cmsFilters}
              browsePageOutput={browsePageOutput}
            />
          </div>
        </div>
      );
    };

    useImperativeHandle(
      ref,
      () => ({
        getHeightRef() {
          return heightRef;
        },
      }),
      []
    );

    // In order to update the height of the hero also when you select a contexual filter button (which is not related to the
    // animation in StickyCollapse), the method that does that needs to be called when the data is changed, which is changed on a
    // contextual filter click.
    useEffect(() => {
      if (specificationRef.current !== browsePageOutput.specification) {
        updateHeroHeightHandler();
      }

      specificationRef.current = browsePageOutput.specification;
    }, [browsePageOutput.specification, updateHeroHeightHandler]);

    return (
      <wwwTrackingContext.Consumer>
        {(trackingCategory) => (
          <StickyCollapse
            openOnMount={openOnMount || browsePageOutput.results.length === 0}
            collapseOnEvent={collapseOnEvent}
            trackingCategory={trackingCategory}
            updateHeroHeight={updateHeroHeightHandler}
            hidden={hidden}
            heightRef={heightRef}
          >
            {(
              collapsed: boolean,
              collapse: (collapsed: boolean) => void,
              animating
            ): React.ReactNode => renderContent(collapsed, collapse, animating)}
          </StickyCollapse>
        )}
      </wwwTrackingContext.Consumer>
    );
  }
);
